/**
 * SaveToListModal
 * ───────────────
 * Global portal modal for saving a title to a watchlist.
 * Replaces the cramped ListPickerDropdown card-overlay with a proper
 * bottom-sheet (mobile) / centered modal (desktop).
 *
 * Usage: dispatch window event "fm-open-save-modal" with { detail: { item } }
 * The modal mounts itself at document.body via createPortal.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Check, Bookmark, Heart } from "lucide-react";
import { useWatchStatus } from "../lib/WatchStatusContext";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const BUILT_IN = [
  { list_id: "later", name: "Watch Later", emoji: "🔖" },
  { list_id: "loved", name: "Loved",       emoji: "❤️" },
];

export default function SaveToListModal() {
  const [item, setItem]           = useState(null);
  const [saving, setSaving]       = useState(null);
  const [creating, setCreating]   = useState(false);
  const [newName, setNewName]     = useState("");
  const [savedTo, setSavedTo]     = useState(new Set()); // list_ids saved in this session
  const inputRef                  = useRef(null);
  const { lists, saveToList, addList, reload } = useWatchStatus();
  const { user } = useAuth();

  // Listen for the global open event
  useEffect(() => {
    const handler = (e) => {
      setItem(e.detail?.item || null);
      setCreating(false);
      setNewName("");
      setSavedTo(new Set());
      setSaving(null);
    };
    window.addEventListener("fm-open-save-modal", handler);
    return () => window.removeEventListener("fm-open-save-modal", handler);
  }, []);

  // Focus input when create form opens
  useEffect(() => {
    if (creating) setTimeout(() => inputRef.current?.focus(), 60);
  }, [creating]);

  // Lock body scroll while open
  useEffect(() => {
    if (item) document.body.style.overflow = "hidden";
    else       document.body.style.overflow = "";
    return ()  => { document.body.style.overflow = ""; };
  }, [item]);

  const close = useCallback(() => setItem(null), []);

  const customLists = (lists || []).filter(
    l => !["later", "loved", "watched"].includes(l.list_id)
  );
  const allLists = [...BUILT_IN, ...customLists];

  const handleSave = async (e, listId) => {
    e.stopPropagation();
    if (!item) return;
    setSaving(listId);
    try {
      await saveToList(item.media_type, item.tmdb_id, item, listId);
      setSavedTo(prev => new Set([...prev, listId]));
      const name = allLists.find(l => l.list_id === listId)?.name || "list";
      toast.success(`Saved to ${name}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Watchlist full — upgrade to Pro for unlimited saves.", {
          action: { label: "Go Pro →", onClick: () => window.dispatchEvent(new CustomEvent("fm-open-paywall")) },
        });
      } else {
        toast.error("Couldn't save — try again");
      }
    } finally {
      setSaving(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const name = newName.trim();
    if (!name || !item) return;
    setSaving("__new__");
    try {
      const r = await api.post("/watchlist/lists", { name, emoji: "📋" });
      const newListId = r.data?.list_id;
      if (newListId) {
        // Optimistically add list to global context so ALL pickers see it immediately
        addList({ list_id: newListId, name, emoji: "📋" });
        await saveToList(item.media_type, item.tmdb_id, item, newListId);
        setSavedTo(prev => new Set([...prev, newListId]));
        toast.success(`Saved to "${name}"`);
        setCreating(false);
        setNewName("");
        // Background reload to sync server state
        reload();
      }
    } catch {
      toast.error("Couldn't create list");
    } finally {
      setSaving(null);
    }
  };

  if (!item) return null;

  const posterUrl = item.poster_path
    ? (item.poster_path.startsWith("http")
        ? item.poster_path
        : `https://image.tmdb.org/t/p/w185${item.poster_path}`)
    : null;

  const title      = item.title || item.name || "";
  const year       = item.release_year || (item.release_date || item.first_air_date || "").slice(0, 4);
  const mediaLabel = item.media_type === "tv" ? "Series" : "Movie";

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      data-testid="save-to-list-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
      />

      {/* Panel */}
      <div
        className="relative w-full sm:w-[420px] max-h-[90vh] sm:max-h-[600px] flex flex-col
                   bg-[#161616] border border-white/10 rounded-t-2xl sm:rounded-2xl
                   shadow-2xl overflow-hidden
                   animate-[slide-up_0.22s_ease-out] sm:animate-[scale-in_0.18s_ease-out]"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header: movie context ── */}
        <div className="flex items-center gap-3 p-4 pb-3 border-b border-white/8">
          {posterUrl ? (
            <img
              src={posterUrl}
              alt={title}
              className="w-11 h-[66px] rounded-md object-cover shrink-0 shadow-lg"
            />
          ) : (
            <div className="w-11 h-[66px] rounded-md bg-white/10 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-0.5">
              Save to list
            </p>
            <p className="text-white font-bold text-sm leading-tight line-clamp-2">{title}</p>
            {year && (
              <p className="text-neutral-500 text-xs mt-0.5">{year} · {mediaLabel}</p>
            )}
          </div>
          <button
            onClick={close}
            className="shrink-0 w-7 h-7 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"
            data-testid="save-modal-close"
          >
            <X className="w-3.5 h-3.5 text-white/70" />
          </button>
        </div>

        {/* ── Quick save (Watch Later + Loved) ── */}
        <div className="flex gap-2.5 px-4 py-3 border-b border-white/8">
          {BUILT_IN.map(l => {
            const saved = savedTo.has(l.list_id);
            return (
              <button
                key={l.list_id}
                onClick={e => handleSave(e, l.list_id)}
                disabled={saving === l.list_id}
                data-testid={`quick-save-${l.list_id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all
                  ${saved
                    ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                    : l.list_id === "loved"
                      ? "bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500/25"
                      : "bg-white/8 border border-white/12 text-white/80 hover:bg-white/14"
                  } disabled:opacity-60`}
              >
                {saved ? (
                  <Check className="w-4 h-4" />
                ) : l.list_id === "loved" ? (
                  <Heart className="w-4 h-4" />
                ) : (
                  <Bookmark className="w-4 h-4" />
                )}
                <span>{l.name}</span>
              </button>
            );
          })}
        </div>

        {/* ── Custom lists ── */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {customLists.length > 0 && (
            <div className="px-4 pt-3 pb-1">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Your Lists
              </p>
              <div className="flex flex-col gap-0.5">
                {customLists.map(list => {
                  const saved = savedTo.has(list.list_id);
                  return (
                    <button
                      key={list.list_id}
                      onClick={e => handleSave(e, list.list_id)}
                      disabled={saving === list.list_id}
                      data-testid={`list-option-${list.list_id}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left w-full transition-all
                        ${saved
                          ? "bg-emerald-500/12 text-emerald-300"
                          : "hover:bg-white/7 text-white/85"
                        } disabled:opacity-60`}
                    >
                      <span className="text-xl leading-none w-7 text-center">{list.emoji}</span>
                      <span className="flex-1 text-sm font-medium">{list.name}</span>
                      {saved && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
                      {saving === list.list_id && (
                        <span className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Create new list ── */}
          <div className="px-4 pt-2 pb-4">
            {!creating ? (
              <button
                onClick={() => setCreating(true)}
                data-testid="create-new-list-btn"
                className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl border border-dashed border-white/15
                           text-neutral-400 hover:text-white hover:border-white/30 hover:bg-white/5
                           text-sm transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Create new list</span>
              </button>
            ) : (
              <form
                onSubmit={handleCreate}
                className="flex gap-2 items-center bg-white/6 border border-white/12 rounded-xl px-3 py-2.5"
              >
                <span className="text-lg">📋</span>
                <input
                  ref={inputRef}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="List name…"
                  maxLength={40}
                  className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-neutral-500"
                  data-testid="new-list-name-input"
                />
                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => { setCreating(false); setNewName(""); }}
                    className="px-3 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!newName.trim() || saving === "__new__"}
                    className="px-3 py-1.5 rounded-lg bg-[#E50914] text-white text-xs font-bold
                               disabled:opacity-50 hover:bg-red-600 transition-colors"
                    data-testid="create-list-submit"
                  >
                    {saving === "__new__" ? "…" : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
